import { GLOBAL_SHORTCUTS } from "@/hooks/useGlobalShortcuts";
import { X, Keyboard } from "lucide-react";

interface ShortcutsHelpDialogProps {
  open: boolean;
  onClose: () => void;
}

const EXTRA_SHORTCUTS = [
  { label: "F1", description: "Toggle Shortcuts Help", category: "navigation" as const },
  { label: "F2", description: "Change Date Field", category: "action" as const },
  { label: "Ctrl+S", description: "Save Current Entry", category: "action" as const },
  { label: "Ctrl+P", description: "Print Current Page", category: "action" as const },
  { label: "Esc", description: "Go Back / Close Dialog", category: "navigation" as const },
  { label: "Tab", description: "Next Field", category: "navigation" as const },
  { label: "Shift+Tab", description: "Previous Field", category: "navigation" as const },
  { label: "↑ ↓", description: "Move Through Menu", category: "navigation" as const },
  { label: "Enter", description: "Select / Confirm", category: "navigation" as const },
  { label: "Alt+N", description: "Create New Item (in invoice)", category: "action" as const },
];

const allShortcuts = [
  ...GLOBAL_SHORTCUTS.filter(s => s.key !== "ctrl+k"), // avoid duplicate
  ...EXTRA_SHORTCUTS.map(s => ({ ...s, key: s.label })),
];

const categories = [
  { key: "voucher", title: "Voucher Entry (Tally-Style)", color: "text-primary" },
  { key: "action", title: "Actions", color: "text-emerald-500" },
  { key: "navigation", title: "Navigation", color: "text-amber-500" },
] as const;

export function ShortcutsHelpDialog({ open, onClose }: ShortcutsHelpDialogProps) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-slate-900/40" />
      <div
        className="relative w-full max-w-2xl bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden max-h-[80vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-white">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600">
              <Keyboard className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Keyboard Shortcuts</h2>
              <p className="text-xs text-slate-500">Press F1 anytime to toggle this help</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 transition-colors text-slate-400 hover:text-slate-700">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto p-6 space-y-6">
          {categories.map(cat => {
            const items = allShortcuts.filter(s => s.category === cat.key);
            if (items.length === 0) return null;
            return (
              <div key={cat.key}>
                <h3 className={`text-xs font-bold uppercase tracking-widest mb-3 ${cat.color}`}>
                  {cat.title}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {items.map((item) => (
                    <div
                      key={item.label + item.description}
                      className="flex items-center justify-between px-3 py-2.5 rounded-lg bg-muted/40 border border-border/50"
                    >
                      <span className="text-sm text-foreground">{item.description}</span>
                      <kbd className="px-2 py-1 rounded bg-background border border-border font-mono text-xs font-semibold text-foreground min-w-[4rem] text-center">
                        {item.label}
                      </kbd>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-border bg-muted/30 text-xs text-muted-foreground flex items-center justify-between">
          <span>These shortcuts work globally across all pages</span>
          <span className="flex items-center gap-1.5">
            <kbd className="px-1.5 py-0.5 rounded bg-background border border-border font-mono text-[10px]">F1</kbd>
            to close
          </span>
        </div>
      </div>
    </div>
  );
}
